import * as React from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  useReactTable,
  getCoreRowModel,
  type SortingState,
  type PaginationState,
} from "@tanstack/react-table";
import { DataTable, DataTablePagination, TableSkeleton } from "@repo/ui";
import { createReceiptColumns } from "./receipt-columns";
import {
  ReceiptFilters,
  type ReceiptFilters as ReceiptFiltersType,
} from "./receipt-filters";
import type { Receipt } from "@/lib/types/inventory";
import { usePaginatedData } from "@/hooks";
import { DrugDetailsDrawer } from "@/components/inventory/drug-details-drawer";

export interface ReceiptHistoryTableProps {
  onView: (receipt: Receipt) => void;
  onEdit?: (receipt: Receipt) => void;
  onDelete?: (receipt: Receipt) => void;
  onEditNotes?: (receipt: Receipt) => void;
  refreshTrigger?: number;
}

export function ReceiptHistoryTable({
  onView,
  onEdit,
  onDelete,
  onEditNotes,
  refreshTrigger,
}: ReceiptHistoryTableProps) {
  const navigate = useNavigate();
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const searchParams = React.useMemo(
    () => new URLSearchParams(searchStr),
    [searchStr]
  );

  // Initialize filters from URL params
  const initialFilters = React.useMemo(() => {
    const filters: ReceiptFiltersType = {};
    if (searchParams.get("dateReceivedFrom")) {
      filters.dateReceivedFrom =
        searchParams.get("dateReceivedFrom") || undefined;
    }
    if (searchParams.get("dateReceivedTo")) {
      filters.dateReceivedTo = searchParams.get("dateReceivedTo") || undefined;
    }
    if (searchParams.get("wholesalerId")) {
      filters.wholesalerId = searchParams.get("wholesalerId") || undefined;
    }
    if (searchParams.get("userIds")) {
      filters.userIds = searchParams.get("userIds") || undefined;
    }
    if (searchParams.get("ndc")) {
      filters.ndc = searchParams.get("ndc") || undefined;
    }
    if (searchParams.get("itemName")) {
      filters.itemName = searchParams.get("itemName") || undefined;
    }
    if (searchParams.get("invoiceNumber")) {
      filters.invoiceNumber = searchParams.get("invoiceNumber") || undefined;
    }
    return filters;
  }, [searchParams]);

  const [filters, setFilters] =
    React.useState<ReceiptFiltersType>(initialFilters);

  // Convert filters to Record<string, string> for the hook
  const filterRecord = React.useMemo<Record<string, string>>(() => {
    const record: Record<string, string> = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        record[key] = value;
      }
    });
    return record;
  }, [filters]);

  // Server-side pagination hook
  const {
    data,
    pagination: paginationInfo,
    isLoading,
    error,
    setPage,
    setPageSize,
    setSorting: setServerSorting,
    setFilters: setServerFilters,
    refetch,
  } = usePaginatedData<Receipt>({
    endpoint: "/receipts",
    defaultPageSize: 50,
    defaultSortBy: "created_at",
    defaultSortOrder: "desc",
    filters: filterRecord,
  });

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [drugDetailsDrawerOpen, setDrugDetailsDrawerOpen] =
    React.useState(false);
  const [selectedNDC, setSelectedNDC] = React.useState<string | null>(null);

  const handleNDCClick = (ndc: string) => {
    setSelectedNDC(ndc);
    setDrugDetailsDrawerOpen(true);
  };

  const handleAddToReminder = React.useCallback(
    (receipt: Receipt) => {
      // item_name may be flattened from the API or nested in inventory
      const itemName =
        (receipt as Receipt & { item_name?: string }).item_name ||
        receipt.inventory?.item_name ||
        receipt.ndc;
      navigate({
        to: `/reminders?action=create&referenceType=receipt&referenceId=${encodeURIComponent(receipt.id)}&referenceName=${encodeURIComponent(itemName)}`,
      });
    },
    [navigate]
  );

  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 50,
    });

  const columns = React.useMemo(
    () =>
      createReceiptColumns(
        onView,
        onEdit,
        onDelete,
        onEditNotes,
        handleNDCClick,
        onEdit || onDelete ? handleAddToReminder : undefined
      ),
    [onView, onEdit, onDelete, onEditNotes, handleAddToReminder]
  );

  // Handle sorting changes — skip on mount (empty initial sorting state)
  const sortingMountedRef = React.useRef(false);
  React.useEffect(() => {
    if (!sortingMountedRef.current) {
      sortingMountedRef.current = true;
      return;
    }
    if (sorting.length > 0) {
      const sort = sorting[0];
      setServerSorting(sort.id, sort.desc ? "desc" : "asc");
    }
  }, [sorting, setServerSorting]);

  // Trigger refetch when refreshTrigger changes
  React.useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Handle filter changes and update URL
  const handleFilterChange = React.useCallback(
    (newFilters: ReceiptFiltersType) => {
      setFilters(newFilters);

      // Convert to Record<string, string> for the hook
      const record: Record<string, string> = {};
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value) {
          record[key] = value;
        }
      });
      setServerFilters(record);

      // Update URL params
      const params = new URLSearchParams(searchParams.toString());

      // Remove all filter params first
      params.delete("dateReceivedFrom");
      params.delete("dateReceivedTo");
      params.delete("wholesalerId");
      params.delete("userIds");
      params.delete("ndc");
      params.delete("itemName");
      params.delete("invoiceNumber");

      // Add new filter params
      if (newFilters.dateReceivedFrom) {
        params.set("dateReceivedFrom", newFilters.dateReceivedFrom);
      }
      if (newFilters.dateReceivedTo) {
        params.set("dateReceivedTo", newFilters.dateReceivedTo);
      }
      if (newFilters.wholesalerId) {
        params.set("wholesalerId", newFilters.wholesalerId);
      }
      if (newFilters.userIds) {
        params.set("userIds", newFilters.userIds);
      }
      if (newFilters.ndc) {
        params.set("ndc", newFilters.ndc);
      }
      if (newFilters.itemName) {
        params.set("itemName", newFilters.itemName);
      }
      if (newFilters.invoiceNumber) {
        params.set("invoiceNumber", newFilters.invoiceNumber);
      }

      navigate({ to: `/receipts?${params.toString()}`, replace: true });
    },
    [setServerFilters, searchParams, navigate]
  );

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const table = useReactTable({
    data,
    columns,
    pageCount: paginationInfo.totalPages,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        if (next.pageIndex !== prev.pageIndex) {
          setPage(next.pageIndex + 1);
        }
        if (next.pageSize !== prev.pageSize) {
          setPageSize(next.pageSize);
        }
        return next;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  const hasActiveFilters = Object.values(filters).some(
    (v) => v !== undefined && v !== ""
  );
  const hasData = data.length > 0;
  const showEmptyState = !isLoading && !hasData;

  return (
    <div className="space-y-4">
      <ReceiptFilters
        onFilterChange={handleFilterChange}
        initialFilters={filters}
      />

      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {showEmptyState && !error && (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <p className="text-lg font-medium text-muted-foreground">
            No receipts found
          </p>
          {hasActiveFilters && (
            <p className="mt-2 text-sm text-muted-foreground">
              Try adjusting your filters to see more results
            </p>
          )}
        </div>
      )}

      {hasData && (
        <>
          <div data-testid="receipt-table">
            <DataTable
              table={table}
              minWidth="1200px"
              getRowTestId={(row) => `receipt-row-${row.original.id}`}
              onRowClick={(row) => onView(row.original)}
            />
          </div>
          <DataTablePagination table={table} />
        </>
      )}

      {isLoading && !hasData && <TableSkeleton columns={10} rows={10} />}

      {selectedNDC && (
        <DrugDetailsDrawer
          open={drugDetailsDrawerOpen}
          onOpenChange={setDrugDetailsDrawerOpen}
          ndc={selectedNDC}
          mode="view"
        />
      )}
    </div>
  );
}
